import {useState} from 'nuxt/app';
import {nextTick} from 'vue';
import {useHandleCode} from '~/composables/useHandleCode';
import {useFetchWithJwt} from '~/composables/useFetchWithJwt';
import {usePersistentState} from '~/composables/usePersistentState';
import {useNotification} from '~/composables/useNotification';
import type {ApiResponseWithPage} from '~/types/response';
import type {BoardData} from '~/types/board';
import type {FaultHistoryData} from "~/types/faultHistory";

export function useAlarm() {
    const {handleErrorCode, handleCodeDepth, showAlarmToast} = useHandleCode();
    const {fetchWithJwt} = useFetchWithJwt();
    const {notify} = useNotification();

    // 전체 알람 정보 (누적된 알람)
    const allAlarmList = usePersistentState<(BoardData | FaultHistoryData)[]>('allAlarmList', () => []);
    // 사용자가 확인한 알람 ID 목록
    const viewedAlarmIds = usePersistentState<string[]>('viewedAlarmIds', () => []);
    // 신규 알람 false/true(기본값 false)
    const isNewAlarm = usePersistentState<boolean>('isNewAlarm', () => false);
    // 최신 서버 알람 ID 목록 (fetchAlarmList에서 받아온 것들)
    const latestServerAlarmIds = usePersistentState<number[]>('latestServerAlarmIds', () => []);

    // 현재 노출되는 공지사항 정보
    const fetchAlarmNoticeList = async (params?: Record<string, any>): Promise<ApiResponseWithPage<BoardData>> => {
        return (
            await fetchWithJwt('/board/new_notice', {
                method: 'POST',
                body: JSON.stringify({
                    ...params
                }),
            })
        ) as ApiResponseWithPage<BoardData>;
    }

    // 현재 노출되는 알람 정보
    const fetchAlarmList = async (params?: Record<string, any>): Promise<ApiResponseWithPage<FaultHistoryData>> => {
        return (
            await fetchWithJwt('/fault_history/search/new_arrivals', {
                method: 'POST',
                body: JSON.stringify({
                    ...params
                }),
            })
        ) as ApiResponseWithPage<FaultHistoryData>;
    }

    // 알림 날짜 포맷 유틸: "2025-05-08T14:30:00" → "2025.05.08.14:30"
    function formatAlarmDate(dt: string) {
        return dt
            .replace('T', ' ')     // "2025-05-08 14:30:00"
            .slice(0, 16)          // "2025-05-08.14:30"
            .replace(/-/g, '.');   // "2025.05.08 14:30"
    }

    /**
     * BoardData vs FaultHistoryData 구분용 Type Guard
     * @param item BoardData | FaultHistoryData
     * @returns item이 BoardData일 경우 true
     */
    function isBoardData(item: BoardData | FaultHistoryData): item is BoardData {
        // BoardData에는 반드시 boardTitle이 있으므로 이를 기준으로 판별
        return 'boardTitle' in item
    }

    // 공지사항 + 알람 정보로 통합된 알람 리스트
    const handleAlarmList = async (params?: Record<string, any>): Promise<void> => {
        try {
            const responseNotice = await fetchAlarmNoticeList(params);
            const responseAlarm = await fetchAlarmList(params);

            if (
                responseNotice && responseNotice.result === 'success' &&
                responseAlarm && responseAlarm.result === 'success'
            ) {
                // 2) 새로 받아온 알람 리스트 중에서 트립내용에 언어팩 적용
                const translatedList = responseAlarm.data.content.map(item => {
                    if (item.faultDescription) {
                        item.faultDescription = handleCodeDepth('faultItem', item.faultItem)
                    }

                    return item;
                });

                // 최신 서버 알람 ID 목록 업데이트 (FaultHistoryData만)
                latestServerAlarmIds.value = translatedList.map(item => item.id);

                // 3) 새로 받아온 공지 + 알람 합치기
                const fetchedList = [
                    ...responseNotice.data.content,
                    ...translatedList
                ];

                // 4) 기존 누적 알람과 새로 받아온 알람을 합치고, 중복 제거
                // 해지된 알람(releaseDatetime이 존재하는 알람)은 제외
                const existingIds = new Set(allAlarmList.value.map(item => item.id));
                const newItems = fetchedList.filter(item => {
                    // 이미 존재하는 알람은 제외
                    if (existingIds.has(item.id)) return false;
                    
                    // 공지사항(BoardData)은 항상 포함
                    if (isBoardData(item)) return true;
                    
                    // FaultHistoryData의 경우 해지되지 않은 알람만 포함
                    return !item.releaseDatetime;
                });
                
                // 새로운 알람을 기존 리스트에 추가하여 누적
                allAlarmList.value = [...allAlarmList.value, ...newItems].sort((a, b) => {
                    try {
                        // faultDatetime이 null/undefined가 아니면 faultDatetime, 아니면 createdDatetime 사용
                        const dateStrA =
                            'faultDatetime' in a && a.faultDatetime
                                ? a.faultDatetime
                                : a.createdDatetime;
                        const dateStrB =
                            'faultDatetime' in b && b.faultDatetime
                                ? b.faultDatetime
                                : b.createdDatetime;

                        const dateA = new Date(dateStrA);
                        const dateB = new Date(dateStrB);

                        // 유효한 날짜인지 확인
                        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                            console.warn('Invalid date detected:', {
                                a: dateStrA,
                                b: dateStrB
                            });
                            return 0;
                        }

                        // 최신 순 정렬
                        return dateB.getTime() - dateA.getTime();
                    } catch (error) {
                        console.error('Date parsing error:', error);
                        return 0;
                    }
                });

                // 5) 새로운 알람이 있고, 사용자가 확인하지 않은 알람이 있으면 isNewAlarm true
                const unviewedAlarms = allAlarmList.value.filter(item => 
                    !viewedAlarmIds.value.includes(item.id.toString())
                );
                
                isNewAlarm.value = unviewedAlarms.length > 0;

                // 6) 새로운 알람이 있으면 알림 표시 (초기 로딩 제외하려면 기존 알람이 있어야 함)
                if (newItems.length > 0) {
                    // 기존 알람이 있는 경우에만 알림 표시 (초기 로딩이 아닌 경우)
                    const shouldNotify = allAlarmList.value.length > newItems.length;
                    
                    newItems.forEach((item) => {
                        if (isBoardData(item)) {
                            // 공지사항의 경우 기존 방식대로 브라우저 알림 사용
                            if (shouldNotify && Notification.permission === 'granted') {
                                const title = item.boardTitle;
                                const body = item.boardContent || '새로운 공지사항이 등록되었습니다.';
                                notify(title, body);
                            }
                        } else {
                            // 알람(FaultHistoryData)의 경우 항상 토스트 메시지 표시 (초기 로딩 포함)
                            if (shouldNotify) {
                                // 실시간 알람은 즉시 표시
                                showAlarmToast(item);
                            } else {
                                // 초기 로딩 시에는 화면 렌더링이 완전히 끝난 후 표시
                                nextTick(() => {
                                    setTimeout(() => {
                                        showAlarmToast(item);
                                    }, 3000); // 3초 딜레이로 증가
                                });
                            }
                            
                            // 브라우저 알림은 초기 로딩이 아닐 때만 표시
                            if (shouldNotify && Notification.permission === 'granted') {
                                const title = item.facility.facilityName || '알람 발생';
                                const body = `${item.terminal.terminalName}\n${handleCodeDepth('faultItem', item.faultItem)}`;
                                notify(title, body);
                            }
                        }
                    });
                }
            } else {
                // 에러 핸들링 그대로
                if (responseNotice && responseNotice.result !== 'success') {
                    handleErrorCode(responseNotice.message);
                } else if (responseAlarm && responseAlarm.result !== 'success') {
                    handleErrorCode(responseAlarm.message);
                } else {
                    handleErrorCode('E50001');
                }
            }
        } catch (error) {
            console.error('Error fetching alarm list:', error);
            handleErrorCode('E50001');
        }
    };

    // 사용자가 알람을 확인했을 때 호출하는 함수
    const clearAlarmNotifications = () => {
        allAlarmList.value = allAlarmList.value.filter(item => {
            // 공지사항(BoardData)인 경우 항상 유지
            if (isBoardData(item)) {
                return true;
            }
            
            // FaultHistoryData인 경우:
            // 1. 최신 서버 알람 목록에 있는 알람만 유지 (서버에서 제공되는 알람)
            // 2. releaseDatetime이 null인 알람만 유지 (아직 해제되지 않은 알람)
            return latestServerAlarmIds.value.includes(item.id);
        });
        
        // 남아있는 알람들을 확인한 것으로 처리
        viewedAlarmIds.value = allAlarmList.value.map(item => item.id.toString());
        // 신규 알람 상태를 false로 변경
        isNewAlarm.value = false;
    };

    return {
        isNewAlarm,
        allAlarmList,
        isBoardData,
        handleAlarmList,
        formatAlarmDate,  // 템플릿에서 사용할 수 있도록 반환
        clearAlarmNotifications, // 알람 확인 함수 추가
    }
}